/**
 * Cart drawer — "The perfect combination" add buttons.
 *
 * Adds a single-variant add-on and hands the refresh to the theme, which
 * re-renders the drawer section. That is why nothing here touches the drawer's
 * markup: the row this button sits in is replaced wholesale a moment later.
 */
/**
 * Section ids the cart renders into, so the add request can return their HTML
 * in the same round trip — see the note in conversion-bundle.js. Without it the
 * drawer opens on its stale, usually empty, markup and only fills in once a
 * second request completes.
 */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((node) => node.dataset.sectionId)
    .filter(Boolean);
}

/**
 * Put the rendered cart HTML into the page before anyone is told the cart
 * changed.
 *
 * Order is the whole point. The drawer opens the moment the event fires, while
 * the cart list morphs a tick later — so the customer sees the drawer's stale
 * contents first. Worse, morphing away from the empty state triggers a view
 * transition, which then plays as a visible sweep across an already-open
 * drawer. Morphing first means the drawer opens on its final contents and that
 * transition never has an empty state to animate from.
 */
async function applyCartSections(sections) {
  if (!sections) return;

  try {
    const { morphSection } = await import('@theme/section-renderer');

    await Promise.all(
      Object.entries(sections).map(([id, html]) => {
        const target = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
        if (!html || !target) return null;

        // The drawer hydrates; a full cart page replaces outright. The
        // stylesheet has to come along, since the filled state adds summary
        // markup the empty state never rendered.
        return morphSection(id, html, {
          mode: target.hasAttribute('data-drawer') ? 'hydration' : 'full',
          injectStylesheet: true,
        });
      })
    );
  } catch (error) {
    // Not fatal: the cart components refetch the section themselves.
    console.warn('[cart] could not pre-render cart sections', error);
  }
}

class CartCombination extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target.closest('[data-cart-combo-add]');
    if (!button || button.disabled) return;

    event.preventDefault();
    const id = Number(button.dataset.variantId);
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;

    const sections = cartSectionIds();
    const payload = { items: [{ id, quantity: 1 }] };
    if (sections.length) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await response.text());
      const result = await response.json();

      await applyCartSections(result?.sections);
      await this.#announce(result?.sections);
    } catch (error) {
      console.warn('[cart-combination] add failed', error);
      // The drawer never refreshed, so this button still exists to restore.
      button.disabled = false;
      button.textContent = original;
    }
  };

  /**
   * Tell the theme the cart changed. The event name comes from Shopify's
   * standard events module rather than a hard-coded string so it cannot drift;
   * a reload is the fallback when that module is unavailable.
   */
  async #announce(sections) {
    let eventName;
    try {
      ({ cartLinesUpdate: eventName } = (await import('@shopify/events')).StandardEvents);
    } catch (error) {
      console.warn('[cart-combination] standard events unavailable', error);
    }

    if (!eventName) {
      window.location.reload();
      return;
    }

    // `action` and `promise` are what the cart components read off this event.
    // `sections` is the pre-rendered cart HTML, so the drawer updates without a
    // second request. No `items` on purpose — /cart/add.js returns only the
    // added line, which the listeners would mistake for the whole cart.
    const event = new Event(eventName, { bubbles: true });
    event.action = 'add';
    event.promise = Promise.resolve({ detail: { source: 'cart-combination', sections } });
    document.dispatchEvent(event);
  }
}

if (!customElements.get('cart-combination')) {
  customElements.define('cart-combination', CartCombination);
}
